import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useAuth } from '../hooks/useAuth';
import { SUPPORTED_LOCALES, LOCALE_LABELS } from '../i18n';
import WorkersManager from '../components/WorkersManager';
import CoresManager from '../components/CoresManager';
export default function Settings() {
    const { t, i18n } = useTranslation();
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [displayName, setDisplayName] = useState('');
    const [locale, setLocale] = useState('en');
    const [backgroundCore, setBackgroundCore] = useState('python');
    const [profileBusy, setProfileBusy] = useState(false);
    const [profileMsg, setProfileMsg] = useState(null);
    const [curPw, setCurPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [pwBusy, setPwBusy] = useState(false);
    const [pwMsg, setPwMsg] = useState(null);
    // Cuando carga el user, inicializa los campos.
    useEffect(() => {
        if (user) {
            setDisplayName(user.displayName ?? '');
            setLocale(user.locale ?? 'en');
            setBackgroundCore(user.backgroundCore ?? 'python');
        }
    }, [user]);
    const saveProfile = async (e) => {
        e.preventDefault();
        setProfileBusy(true);
        setProfileMsg(null);
        try {
            const r = await api.patch('/auth/me', {
                displayName: displayName.trim() === '' ? null : displayName.trim(),
                locale,
                backgroundCore,
            });
            // Cambia el idioma de la UI inmediatamente si cambió el locale.
            if (r.data.locale && r.data.locale !== i18n.resolvedLanguage) {
                void i18n.changeLanguage(r.data.locale);
                document.documentElement.lang = r.data.locale;
            }
            setProfileMsg({ kind: 'ok', text: t('settings.profileSaved') });
        }
        catch (err) {
            setProfileMsg({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
        }
        finally {
            setProfileBusy(false);
        }
    };
    const changePassword = async (e) => {
        e.preventDefault();
        setPwBusy(true);
        setPwMsg(null);
        try {
            await api.post('/auth/change-password', { currentPassword: curPw, newPassword: newPw });
            setPwMsg({ kind: 'ok', text: t('settings.passwordChanged') });
            setCurPw('');
            setNewPw('');
            // El backend revocó todas las sesiones — forzamos logout local + redirect.
            setTimeout(() => {
                void logout();
                nav('/login');
            }, 1500);
        }
        catch (err) {
            setPwMsg({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
        }
        finally {
            setPwBusy(false);
        }
    };
    if (!user)
        return null;
    return (_jsxs("section", { className: "prose", children: [_jsx("h1", { children: t('settings.title') }), _jsxs("article", { className: "card form-card", children: [_jsx("h2", { children: t('settings.profile') }), _jsxs("form", { onSubmit: saveProfile, children: [_jsxs("label", { children: [t('settings.email'), _jsx("input", { type: "email", value: user.email, disabled: true })] }), _jsxs("label", { children: [t('settings.displayName'), _jsx("input", { type: "text", maxLength: 80, value: displayName, onChange: (e) => setDisplayName(e.target.value), placeholder: t('settings.displayNamePlaceholder') })] }), _jsxs("label", { children: [t('common.language'), _jsx("select", { value: locale, onChange: (e) => setLocale(e.target.value), children: SUPPORTED_LOCALES.map((l) => (_jsx("option", { value: l, children: LOCALE_LABELS[l] }, l))) })] }), _jsxs("label", { children: [t('settings.backgroundCore'), _jsxs("select", { value: backgroundCore, onChange: (e) => setBackgroundCore(e.target.value), children: [_jsx("option", { value: "python", children: t('settings.cores.python') }), _jsx("option", { value: "node", children: t('settings.cores.node') }), _jsx("option", { value: "rust", children: t('settings.cores.rust') }), _jsx("option", { value: "cpp", children: t('settings.cores.cpp') }), _jsx("option", { value: "fortran", children: t('settings.cores.fortran') }), _jsx("option", { value: "julia", children: t('settings.cores.julia') }), _jsx("option", { value: "mathematica", children: t('settings.cores.mathematica') })] })] }), profileMsg && (_jsx("p", { className: profileMsg.kind === 'ok' ? 'ok-text' : 'error', children: profileMsg.text })), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: profileBusy, children: profileBusy ? t('common.loading') : t('settings.saveProfile') })] })] }), _jsxs("article", { className: "card form-card", children: [_jsx("h2", { children: t('settings.changePassword') }), _jsx("p", { className: "muted", children: t('settings.changePasswordNote') }), _jsxs("form", { onSubmit: changePassword, children: [_jsxs("label", { children: [t('settings.currentPassword'), _jsx("input", { type: "password", required: true, value: curPw, onChange: (e) => setCurPw(e.target.value) })] }), _jsxs("label", { children: [t('settings.newPassword'), _jsx("input", { type: "password", required: true, minLength: 10, value: newPw, onChange: (e) => setNewPw(e.target.value) })] }), pwMsg && (_jsx("p", { className: pwMsg.kind === 'ok' ? 'ok-text' : 'error', children: pwMsg.text })), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: pwBusy || curPw.length < 1 || newPw.length < 10, children: pwBusy ? t('common.loading') : t('settings.changePassword') })] })] }), _jsx(WorkersManager, {}), _jsx(CoresManager, {})] }));
}
